import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, X, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, downloadFile } from "@/lib/api";
import { toast } from "sonner";

const usePageImage = (projectId, n, w, enabled) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled || !n) return undefined;
    let revoked = false; let obj = null;
    api.get(`/projects/${projectId}/export/preview/page/${n}`, { params: { w }, responseType: "blob" })
      .then((r) => { if (!revoked) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [projectId, n, w, enabled]);
  return url;
};

const Thumb = ({ projectId, n, title, active, onClick }) => {
  const url = usePageImage(projectId, n, 220, true);
  return (
    <button type="button" onClick={onClick} className={`shrink-0 w-[112px] text-left rounded-md border p-1 transition-colors ${active ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-400 bg-white"}`} data-testid={`pack-thumb-${n}`}>
      <div className="aspect-[1.414] w-full bg-slate-100 rounded overflow-hidden flex items-center justify-center">
        {url ? <img src={url} alt={`Page ${n}`} className="w-full h-full object-contain" /> : <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      <p className="mt-1 text-[9px] leading-tight text-slate-600 truncate"><span className="font-semibold text-slate-800">{n}</span> · {title}</p>
    </button>
  );
};

export const PackPreview = ({ projectId, open, onClose, prepare }) => {
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [status, setStatus] = useState("");
  const [dl, setDl] = useState(false);
  const stripRef = useRef(null);
  const big = usePageImage(projectId, page, 1600, !!meta);
  usePageImage(projectId, meta && page < meta.pages ? page + 1 : 0, 1600, !!meta); // prefetch next

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setStatus("Preparing narrative…");
        await prepare?.();
        if (cancelled) return;
        setStatus("Rendering Budget Pack…");
        const { data } = await api.post(`/projects/${projectId}/export/preview`);
        if (cancelled) return;
        setMeta(data); setPage(1); setStatus("");
      } catch (e) { toast.error(e?.response?.data?.detail || e.message); setStatus("Preview failed"); }
    })();
    return () => { cancelled = true; };
  }, [open, projectId, prepare]);

  useEffect(() => {
    const onKey = (e) => { if (!open) return; if (e.key === "ArrowRight") setPage((p) => Math.min(meta?.pages || 1, p + 1)); if (e.key === "ArrowLeft") setPage((p) => Math.max(1, p - 1)); if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [open, meta, onClose]);

  useEffect(() => { stripRef.current?.querySelector(`[data-testid="pack-thumb-${page}"]`)?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" }); }, [page]);

  if (!open) return null;
  const download = async () => {
    setDl(true);
    try { await downloadFile(`/projects/${projectId}/export/preview/pdf`, "budget-pack.pdf"); toast.success("Budget Pack PDF downloaded"); } catch (e) { toast.error(e.message); } finally { setDl(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-slate-900/80 backdrop-blur-sm flex flex-col" style={{ height: "100dvh" }} data-testid="pack-preview">
      <div className="flex items-center justify-between gap-3 px-4 h-12 bg-slate-950 text-white shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">Budget Pack preview {meta ? <span className="text-slate-400 font-normal">· page {page} of {meta.pages}</span> : null}</p>
          {meta?.titles?.[page - 1] && <p className="text-[11px] text-slate-400 truncate" data-testid="pack-page-title">{meta.titles[page - 1]}</p>}
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" className="h-8 text-white hover:bg-slate-800 hidden sm:inline-flex" onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.2).toFixed(1)))} data-testid="pack-zoom-out"><ZoomOut size={15} /></Button>
          <span className="text-[11px] w-10 text-center text-slate-300 hidden sm:inline">{Math.round(zoom * 100)}%</span>
          <Button size="sm" variant="ghost" className="h-8 text-white hover:bg-slate-800 hidden sm:inline-flex" onClick={() => setZoom((z) => Math.min(2, +(z + 0.2).toFixed(1)))} data-testid="pack-zoom-in"><ZoomIn size={15} /></Button>
          <Button size="sm" onClick={download} disabled={!meta || dl} className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white sm:ml-2" data-testid="pack-preview-download"><Download size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">{dl ? "Downloading…" : "Download this PDF"}</span></Button>
          <Button size="sm" variant="ghost" className="h-8 text-white hover:bg-slate-800" onClick={onClose} data-testid="pack-preview-close"><X size={16} /></Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto flex items-start justify-center p-3 sm:p-6 relative" data-testid="pack-page-view">
          {!meta ? (
            <div className="m-auto text-center text-slate-200"><Loader2 size={28} className="animate-spin mx-auto mb-3" /><p className="text-sm" data-testid="pack-preview-status">{status || "Loading…"}</p><p className="text-xs text-slate-400 mt-1">First preview takes up to a minute while the narrative is generated.</p></div>
          ) : big ? (
            <img src={big} alt={`Budget Pack page ${page}`} style={{ width: `${Math.min(100, 92 * zoom)}%`, maxWidth: `${1200 * zoom}px` }} className="shadow-2xl bg-white rounded-sm" data-testid="pack-page-image" />
          ) : <Loader2 size={24} className="animate-spin text-slate-300 m-auto" />}
          {meta && (<>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="fixed left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 hover:bg-white p-2 shadow disabled:opacity-30" data-testid="pack-prev"><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => setPage((p) => Math.min(meta.pages, p + 1))} disabled={page >= meta.pages} className="fixed right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 hover:bg-white p-2 shadow disabled:opacity-30" data-testid="pack-next"><ChevronRight size={18} /></button>
          </>)}
        </div>
        {meta && (
          <div ref={stripRef} className="shrink-0 h-[132px] bg-slate-100 border-t border-slate-300 flex gap-2 overflow-x-auto px-3 py-2" data-testid="pack-thumbs">
            {Array.from({ length: meta.pages }, (_, i) => <Thumb key={i} projectId={projectId} n={i + 1} title={meta.titles?.[i] || ""} active={page === i + 1} onClick={() => setPage(i + 1)} />)}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
